/**
 * 「换我出镜」内容区(0924 车 D 从 VideoRealCreatePage 拆出 + 一期止血)。
 *
 * 在拆出前的上传/体检/补充说明之上,本批加三件(契约 §3.4):
 *   ① 时长按档:15~30s 且当前不是 2.5 → 琥珀提示 + 「切到「旗舰」档并重新报价」一键切;
 *      >30s → 红字拦(上传即拦,文件没传);
 *   ② 疑似转发/转码件只警示(wxMeta / Lavf / 低码率);
 *   ③ 参考视频要求说清楚(自己手机拍的原件、时长档、别用转发件、被拒不扣费)——
 *      不再只有一句「涉及版权或公众人物会生成失败」。
 */
import { CheckCircle2, Loader2, UploadCloud } from "lucide-react";

import { Button } from "@/shared/ui/button";
import { Checkbox } from "@/shared/ui/checkbox";
import { Label } from "@/shared/ui/label";
import { Textarea } from "@/shared/ui/textarea";
import { cn } from "@/shared/lib/utils";
import { VIDEO_ACCEPT } from "@/shared/lib/uploadImageFormats";
import {
  SWAP_ABSOLUTE_MAX_SEC,
  swapVerdictText,
  type SwapDurationVerdict,
} from "../../../model/realPersonSwapTier";
import type { SwapReferenceState } from "./useSwapReference";

export function RealPersonSwapPanel({
  swap,
  verdict,
  modelLabel,
  modelMaxSec,
  onSwitchLongTier,
  note,
  onNoteChange,
  usageRightConfirmed,
  onUsageRightConfirmedChange,
}: {
  swap: SwapReferenceState;
  verdict: SwapDurationVerdict;
  /** 当前模型档简称(拼提示句用)。 */
  modelLabel: string;
  modelMaxSec: number;
  onSwitchLongTier: () => void;
  note: string;
  onNoteChange: (v: string) => void;
  /**
   * D2(0925 收尾波):第三方视频换脸合规口 —— 默认不勾,勾选状态随任务请求体
   * 发 `swapUsageRightConfirmed`(见 VideoRealCreatePage;api 本波未接字段,前端先发)。
   */
  usageRightConfirmed: boolean;
  onUsageRightConfirmedChange: (v: boolean) => void;
}) {
  const { file, issue, checking, uploading, objectKey, forwardedHint } = swap;
  const tierText = swapVerdictText(verdict, modelLabel);
  return (
    <div className="space-y-3" data-testid="real-swap-panel">
      <button
        type="button"
        data-testid="swap-upload-zone"
        onClick={() => document.getElementById("swap-video-input")?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          void swap.onPickFile(e.dataTransfer.files?.[0]);
        }}
        className="flex w-full flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border/70 px-6 py-8 text-center text-sm text-muted-foreground transition-colors hover:border-primary/60 hover:text-primary"
      >
        <UploadCloud className="h-6 w-6" />
        {file ? (
          <span className="text-foreground">已选「{file.name}」,点击换一个</span>
        ) : (
          <span>点击选择或拖拽参考视频到这里</span>
        )}
      </button>
      <input
        id="swap-video-input"
        type="file"
        accept={VIDEO_ACCEPT}
        className="hidden"
        aria-label="选择参考视频"
        onChange={(e) => void swap.onPickFile(e.target.files?.[0])}
      />
      {/* D2(0925 收尾波):第三方视频换脸合规口 —— 只写一句在页面上留不住证据,
          勾选 + 随请求体留档才算数(与 poster TryonCreatePage 的肖像授权勾选同一手法)。 */}
      <label className="flex items-start gap-2 text-xs text-muted-foreground">
        <Checkbox
          checked={usageRightConfirmed}
          onCheckedChange={(v) => onUsageRightConfirmedChange(v === true)}
          data-testid="swap-usage-right-checkbox"
          aria-label="我确认对该视频拥有使用权并已获得出镜者授权"
        />
        <span>我确认对该视频拥有使用权并已获得出镜者授权</span>
      </label>
      {(checking || uploading) && (
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" />
          {checking ? "体检中…" : "上传中…"}
        </p>
      )}
      {issue && !checking && (
        <p
          data-testid="swap-video-issue"
          className={cn(
            "text-xs",
            issue.blocksSubmit ? "text-destructive" : "text-amber-600 dark:text-amber-400"
          )}
        >
          {issue.text}
          {issue.blocksSubmit ? "(这个文件不会用于出片)" : "(仍会出片,仅提醒)"}
        </p>
      )}
      {tierText && !checking && (
        <div
          data-testid="swap-tier-notice"
          data-verdict={verdict.kind}
          className={cn(
            "flex flex-wrap items-center gap-2 rounded-md border px-3 py-2 text-xs leading-relaxed",
            verdict.kind === "over_max"
              ? "border-destructive/40 bg-destructive/5 text-destructive"
              : "border-amber-400/40 bg-amber-500/5 text-amber-700 dark:text-amber-400"
          )}
        >
          <span className="min-w-0 flex-1">{tierText}</span>
          {verdict.kind === "needs_long_tier" && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              data-testid="swap-switch-long-tier"
              onClick={onSwitchLongTier}
            >
              切到「旗舰」档并重新报价
            </Button>
          )}
        </div>
      )}
      {forwardedHint && !checking && (
        <p data-testid="swap-forwarded-hint" className="text-xs text-amber-600 dark:text-amber-400">
          {forwardedHint}(仍会出片,仅提醒)
        </p>
      )}
      {objectKey && !checking && !uploading && verdict.kind === "ok" && (
        <p className="flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400">
          <CheckCircle2 className="h-3.5 w-3.5" />
          已就绪,出片时长跟随参考视频(当前档最长 {modelMaxSec} 秒)
        </p>
      )}
      <div className="space-y-1.5">
        <Label htmlFor="swap-note">补充说明(可选)</Label>
        <Textarea
          id="swap-note"
          value={note}
          maxLength={200}
          rows={2}
          placeholder="视频里有多人时请说明换谁,例:把左边穿蓝色衣服的人换成我"
          onChange={(e) => onNoteChange(e.target.value)}
        />
      </div>
      <p className="text-2xs leading-relaxed text-muted-foreground" data-testid="swap-requirements">
        参考视频要求:用你自己手机直接拍的原件;15 秒以内任意档,15~{SWAP_ABSOLUTE_MAX_SEC}{" "}
        秒需「旗舰」档,超过 {SWAP_ABSOLUTE_MAX_SEC}{" "}
        秒不支持;别用微信转发件、下载的别人视频或剪辑过的视频
        (画面里有他人/公众人物、涉及版权都会被拒)。被拒不扣费。
      </p>
    </div>
  );
}
